import {
  Button,
  CloseButton,
  Dialog,
  Field,
  Fieldset,
  GridItem,
  Input,
  Portal,
  SimpleGrid,
  Skeleton,
  Switch,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { BannerPlatform } from '@client/graphql/types/graphql';
import { useFocusedCompanyContext } from '@packages/core/company/focused-company-context';
import { toaster } from '@packages/provider/module/chakra-ui/components/ui/toaster';
import { useTranslate } from '@tolgee/react';
import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useCreateTopBanners } from '../../logics/top-banner/use-create-top-banner';
import BannerPlatformSelection from './banner-platform-selection';
import { ColorPicker } from './color-picker';
import { COMMON_COLORS } from './common-color';
interface FormValues {
  title: string;
  description: string;
  startTime: string;
  endTime: string;
  isActive: boolean;
  platforms: BannerPlatform[];
  backgroundColor: string;
}

export default function TopBannerCreateModal() {
  const { t } = useTranslate();
  const company = useFocusedCompanyContext();
  const [create, { loading, reset: createReset }] = useCreateTopBanners();
  const [open, setOpen] = useState(false);

  const {
    watch,
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      title: '',
      description: '',
      startTime: moment.tz(company.settings.timezone).startOf('date').format('YYYY-MM-DDTHH:mm'),
      endTime: moment.tz(company.settings.timezone).format('YYYY-MM-DDTHH:mm'),
      isActive: true,
      platforms: [],
      backgroundColor: COMMON_COLORS.at(0),
    },
  });

  useEffect(() => {
    reset();
    createReset();
  }, [createReset, open, reset]);

  const onSubmit = () => {
    create({
      variables: {
        companyId: company.id,
        input: {
          title: watch('title'),
          description: watch('description'),
          startTime: watch('startTime'),
          endTime: watch('endTime'),
          isActive: watch('isActive'),
          platforms: watch('platforms'),
          backgroundColor: watch('backgroundColor'),
        },
      },
    })
      .then(() => {
        toaster.create({
          description: t('top-banner.create.alert', 'Create top banner successful'),
          type: 'info',
        });
        setOpen(false);
      })
      .catch(() => {
        toaster.create({
          description: t('top-banner.create.alert.failed', 'Create top banner failed'),
          type: 'error',
        });
      });
  };

  const FieldSet = (
    <Fieldset.Root size="lg" maxW="md">
      <Fieldset.Content>
        <Field.Root>
          <Field.Label>{t('top-banner.create.title', 'Title')}</Field.Label>
          <Input
            borderColor={'gray.300'}
            rounded="sm"
            {...register('title', {
              required: t('top-banner.create.title-required', 'Title is required'),
              maxLength: {
                value: 200,
                message: t('top-banner.create.title-max-length', 'Title must be less than 200 characters'),
              },
            })}
          />
          <Field.HelperText>
            {watch('title').length || 0}/200 {t('common.characters', 'characters')}
          </Field.HelperText>
          {errors.title && (
            <Text color="red.500" fontSize="sm">
              {errors.title.message}
            </Text>
          )}
        </Field.Root>

        <Field.Root>
          <Field.Label>{t('top-banner.create.description', 'Description')}</Field.Label>
          <Textarea
            borderColor={'gray.300'}
            rounded="sm"
            minH="100px"
            {...register('description', {
              required: t('top-banner.create.description-required', 'Description is required'),
            })}
          />
          {errors.description && (
            <Text color="red.500" fontSize="sm">
              {errors.description.message}
            </Text>
          )}
        </Field.Root>

        <SimpleGrid gap={2} columns={2}>
          <GridItem>
            <Field.Root>
              <Field.Label>{t('top-banner.create.start-time', 'Start Time')}</Field.Label>
              <Input
                type="datetime-local"
                borderColor={'gray.300'}
                rounded="sm"
                {...register('startTime', {
                  required: t('top-banner.create.start-time-required', 'Start time is required'),
                  validate: (value) => {
                    const endTime = watch('endTime');
                    if (value >= endTime) {
                      return t('top-banner.create.start-time-before-end', 'Start time must be before end time');
                    }
                    return true;
                  },
                })}
              />
              {errors.startTime && (
                <Text color="red.500" fontSize="sm">
                  {errors.startTime.message}
                </Text>
              )}
            </Field.Root>
          </GridItem>

          <GridItem>
            <Field.Root>
              <Field.Label>{t('top-banner.create.end-time', 'End Time')}</Field.Label>
              <Input
                type="datetime-local"
                borderColor={'gray.300'}
                rounded="sm"
                {...register('endTime', {
                  required: t('top-banner.create.end-time-required', 'End time is required'),
                  validate: (value) => {
                    const startTime = watch('startTime');
                    if (value <= startTime) {
                      return t('top-banner.create.end-time-after-start', 'End time must be after start time');
                    }
                    return true;
                  },
                })}
              />
              {errors.endTime && (
                <Text color="red.500" fontSize="sm">
                  {errors.endTime.message}
                </Text>
              )}
            </Field.Root>
          </GridItem>
        </SimpleGrid>

        <Field.Root>
          <Field.Label>{t('top-banner.create.platforms', 'Platforms')}</Field.Label>
          <input
            type="hidden"
            {...register('platforms', {
              required: t('top-banner.create.platforms-required', 'At least one platform is required'),
              validate: (value) =>
                value.length > 0 || t('top-banner.create.platforms-required', 'At least one platform is required'),
            })}
          />
          <BannerPlatformSelection
            platform={watch('platforms')}
            onChange={(value: BannerPlatform[]) => {
              setValue('platforms', value);
            }}
          />
          {errors.platforms && (
            <Text color="red.500" fontSize="sm">
              {errors.platforms.message}
            </Text>
          )}
        </Field.Root>

        <Field.Root>
          <Field.Label>{t('top-banner.create.is-active', 'Active Status')}</Field.Label>
          <Switch.Root
            checked={watch('isActive')}
            onCheckedChange={({ checked }) => {
              setValue('isActive', checked);
            }}
          >
            <Switch.HiddenInput />
            <Switch.Control />
          </Switch.Root>
          {errors.isActive && (
            <Text color="red.500" fontSize="sm">
              {errors.isActive.message}
            </Text>
          )}
        </Field.Root>

        <Field.Root>
          <Field.Label>{t('top-banner.create.background-color', 'Background Color')}</Field.Label>
          <input
            type="hidden"
            {...register('backgroundColor', {
              required: t('top-banner.create.background-color-required', 'Background color is required'),
              validate: (value) =>
                value.trim() !== '' || t('top-banner.create.background-color-required', 'Background color is required'),
            })}
          />
          <ColorPicker
            selectedColor={watch('backgroundColor')}
            onColorSelect={(color) => {
              setValue('backgroundColor', color);
            }}
          />
          {errors.backgroundColor && (
            <Text color="red.500" fontSize="sm">
              {errors.backgroundColor.message}
            </Text>
          )}
        </Field.Root>
      </Fieldset.Content>
    </Fieldset.Root>
  );

  return (
    <Dialog.Root
      lazyMount
      open={open}
      onOpenChange={(e) => {
        setOpen(e.open);
      }}
      placement={'center'}
    >
      <Dialog.Trigger asChild>
        <Button bgColor={'gray.800'} p={2} color={'white'}>
          {t('common.button.create', 'Create')}
        </Button>
      </Dialog.Trigger>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title fontSize={'xl'}>{t('top-banner.dialog.create.title', 'Create Top Banner')}</Dialog.Title>
            </Dialog.Header>

            <Dialog.Body>
              {loading ? (
                <Fieldset.Root size="lg" maxW="md">
                  <Fieldset.Content>
                    <Field.Root>
                      <Field.Label>{t('top-banner.create.title', 'Title')}</Field.Label>
                      <Skeleton height="40px" width="200px" />
                    </Field.Root>

                    <Field.Root>
                      <Field.Label>{t('top-banner.create.description', 'Description')}</Field.Label>
                      <Skeleton height="40px" width="200px" />
                    </Field.Root>

                    <SimpleGrid gap={2} columns={2}>
                      <GridItem>
                        <Field.Root>
                          <Field.Label>{t('top-banner.create.start-time', 'Start Time')}</Field.Label>
                          <Skeleton height="40px" width="200px" />
                        </Field.Root>
                      </GridItem>

                      <GridItem>
                        <Field.Root>
                          <Field.Label>{t('top-banner.create.end-time', 'End Time')}</Field.Label>
                          <Skeleton height="40px" width="200px" />
                        </Field.Root>
                      </GridItem>
                    </SimpleGrid>

                    <Field.Root>
                      <Field.Label>{t('top-banner.create.platforms', 'Platforms')}</Field.Label>
                      <Skeleton height="40px" width="200px" />
                    </Field.Root>

                    <Field.Root>
                      <Field.Label>{t('top-banner.create.is-active', 'Active Status')}</Field.Label>
                      <Skeleton height="40px" width="200px" />
                    </Field.Root>

                    <Field.Root>
                      <Field.Label>{t('top-banner.create.background-color', 'Background Color')}</Field.Label>
                      <Skeleton height="40px" width="200px" />
                    </Field.Root>
                  </Fieldset.Content>
                </Fieldset.Root>
              ) : (
                FieldSet
              )}
            </Dialog.Body>
            <Dialog.Footer>
              <Dialog.ActionTrigger asChild>
                <Button variant="surface" p={2}>
                  {t('common.button.cancel', 'Cancel')}
                </Button>
              </Dialog.ActionTrigger>
              <Button
                loading={loading}
                onClick={() => {
                  void handleSubmit(onSubmit)();
                }}
                p={2}
                bg={'gray.700'}
                variant={'surface'}
                color={'white'}
                type="submit"
              >
                {t('common.button.create')}
              </Button>
            </Dialog.Footer>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
